/**
 * demoClientData.ts (shared) — the bundled fixture the Control Center renders in
 * demo mode (see demoMode.ts). A believable "Treasure Valley Plumbing" with leads,
 * agent activity, reports, approvals, and invoices so the no-login Zoom showcase
 * looks fully populated WITHOUT touching Firestore or exposing any real client.
 *
 * useClientData() early-returns DEMO_CLIENT_DATA when isDemoMode() is true. All
 * data here is fake. Tweak freely — it's a marketing surface, not production logic.
 */
import type { ClientDocument } from './hooks/useClientAuth'
import type { ClientData } from './hooks/useClientData'
import { REFERRAL_CREDIT } from './theme'

/** Firestore-Timestamp duck-type so UI formatters (.toDate()/.toMillis()/.seconds) work. */
function ts(daysAgo: number): unknown {
  const d = new Date(Date.now() - daysAgo * 86400_000)
  return { seconds: Math.floor(d.getTime() / 1000), nanoseconds: 0, toDate: () => d, toMillis: () => d.getTime() }
}

// The referred businesses the demo showcase lists. Declared here rather than
// beside `demoReferrals` below because DEMO_CLIENT's referral numbers are derived
// from it — see the note on `referralCreditBalanceCents`.
const REFERRAL_BUSINESSES = [
  'Canyon Ridge Electric', 'Boise Bench Roofing', 'Silver Creek HVAC',
  'Eagle Point Landscaping', 'Meridian Garage Doors', 'Kuna Pest Control',
  'Nampa Window Cleaning',
];

/** The fake client doc — populates every field the Control Center renders. */
export const DEMO_CLIENT = {
  id: 'demo-treasure-valley-plumbing',
  businessName: 'Treasure Valley Plumbing',
  name: 'Treasure Valley Plumbing',
  ownerName: 'Mike Thompson',
  trade: 'Plumber',
  phone: '+12085550123',
  email: 'mike@treasurevalleyplumbing.com',
  status: 'active',
  billingStatus: 'active',
  hiredAgents: ['google', 'phone', 'social', 'web', 'nurture'],
  monthlyBill: 450,
  // ⚠️ RECOMPUTED AT THE REAL RATE (2026-09-13). This read "7 active referrals
  // earn $525/mo against a $450 bill … so $75 carries to next month" — arithmetic
  // at $75 per referral, the rate RETIRED on 2026-08-09 in favour of $37.50. The
  // prospect-facing showcase was overstating the benefit by 2x using our own
  // superseded pricing. [[feedback_never_invent_a_stat_client_content]]
  //
  // At $37.50: 7 active x $37.50 = $262.50 earned against a $450 bill, so all of
  // it applies and NOTHING carries. The carried-forward line therefore shows
  // nothing — the honest demo of a feature that only triggers once earnings
  // exceed the bill, rather than a fabricated $75.
  referralCreditBalanceCents: 0,
  // `creditPerMonth: 150` REMOVED 2026-09-13. Measured before deleting: zero
  // backend references, zero consumers in app/, and 0 of 6 real client documents
  // carry the field. It was a fixture-only invention, and its value was two
  // referrals at the RETIRED $75 rate — so it made the demo look like it modelled
  // a real field while quoting a price we no longer offer.
  referralCode: 'MIKE-TVP',
  franchiseNetworkId: null,
  joinedAt: ts(168),
  // ⚠️ A STRING, because `ClientDocument.address` is declared `address?: string`
  // and all six real clients that have one store a string. This held an OBJECT —
  // `{street, city, state, zip}` — which the `as unknown as` cast below let
  // through, and `Documents.tsx` renders it directly: React threw "Objects are
  // not valid as a React child" and the New Document composer came up BLANK in
  // demo mode. Demo mode is what prospects are shown on a call.
  address: '1420 W Overland Rd, Boise, ID 83705',
  locations: [],
  passThroughBilling: { domain: 12, workspace: 7.2 },
  styleProfiles: {},
} as unknown as ClientDocument

const LEAD_SOURCES = ['google', 'phone', 'facebook', 'website', 'referral']
const LEAD_STATUSES = ['new', 'contacted', 'booked', 'booked', 'won', 'nurture']
const LEAD_NAMES = [
  'Sarah Martinez', 'Tom Henderson', 'Linda Chen', 'David Okafor', 'Emily Rossi',
  'James Park', 'Maria Gomez', 'Robert Webb', 'Aisha Khan', 'Carlos Diaz',
  'Nancy Pratt', 'Greg Sullivan',
]

const leads = LEAD_NAMES.map((name, i) => ({
  id: `demo-lead-${i + 1}`,
  contactId: `demo-contact-${i + 1}`,
  clientId: DEMO_CLIENT.id,
  name,
  phone: `+1208555${String(2000 + i).padStart(4, '0')}`,
  email: `${name.split(' ')[0].toLowerCase()}@example.com`,
  source: LEAD_SOURCES[i % LEAD_SOURCES.length],
  channel: LEAD_SOURCES[i % LEAD_SOURCES.length],
  status: LEAD_STATUSES[i % LEAD_STATUSES.length],
  interactions: [
    { type: 'inbound_call', channel: 'phone', summary: 'Asked about a water heater replacement quote.', timestamp: ts(i + 1) },
  ],
  notes: [],
  createdAt: ts(i + 2),
  lastInteractionAt: ts(i),
})) as unknown as ClientData['leads']

const agentLogs = [
  ...Array.from({ length: 14 }, (_, i) => ({ id: `dl-call-${i}`, clientId: DEMO_CLIENT.id, agentId: 'phone', taskType: 'call_answered', status: 'success', category: 'ongoing', output: {}, cost: 0.04, durationMs: 180000, timestamp: ts(i) })),
  ...Array.from({ length: 9 }, (_, i) => ({ id: `dl-post-${i}`, clientId: DEMO_CLIENT.id, agentId: 'social', taskType: 'post_published', status: 'success', category: 'ongoing', output: {}, cost: 0.03, durationMs: 5000, timestamp: ts(i) })),
  ...Array.from({ length: 6 }, (_, i) => ({ id: `dl-rev-${i}`, clientId: DEMO_CLIENT.id, agentId: 'google', taskType: 'review_request', status: 'success', category: 'ongoing', output: {}, cost: 0.01, durationMs: 2000, timestamp: ts(i) })),
] as unknown as ClientData['agentLogs']

const weeklyReports = Array.from({ length: 4 }, (_, i) => ({
  id: `dl-wr-${i}`, clientId: DEMO_CLIENT.id, weekStarting: ts(7 * (i + 1)), template: i % 2 === 0 ? 'A' : 'B',
  // Field names must match Reports.tsx firestoreToReportEntry (callsAnswered/
  // postsPublished/reviewsRequested/leadsGenerated/notableEvents) or it renders 0s.
  metrics: {
    callsAnswered: 14 - i * 2,
    postsPublished: 9 - i,
    reviewsRequested: 4 - (i % 2),
    leadsGenerated: 9 - i,
    notableEvents: [
      'New 5-star Google review from Sarah M.',
      `Phone Skill booked ${3 - (i % 2)} jobs after hours`,
      'Instagram reel hit 2,400 views',
    ],
  },
  sentAt: ts(7 * (i + 1)), emailOpened: true, smsDelivered: true,
})) as unknown as ClientData['weeklyReports']

const actionItems = [
  { id: 'dl-ai-1', clientId: DEMO_CLIENT.id, type: 'connect_stripe', title: 'Connect Stripe', description: 'So Ava can send review requests the moment a job is paid.', deepLink: '/control-center/settings', priority: 'high', status: 'open', createdAt: ts(3) },
  { id: 'dl-ai-2', clientId: DEMO_CLIENT.id, type: 'import_contacts', title: 'Import your customer list', description: 'Let your Nurture Skill reactivate past customers.', deepLink: '/control-center/settings', priority: 'medium', status: 'open', createdAt: ts(5) },
] as unknown as ClientData['actionItems']

/**
 * THE APPROVAL QUEUE — the showcase's most-clicked surface.
 *
 * ⚠️ ADDED 2026-07-14, because I broke this. The Approvals page used to render seven INVENTED items
 * from a hardcoded array in the page file — shown to every REAL client, who saw content awaiting
 * their approval that did not exist. I deleted that array and wired the page to the real
 * `pendingApprovals` subcollection… which `useSubcollection` reads straight from Firestore, with no
 * demo-mode branch. So the fix left the DEMO showing an empty queue.
 *
 * That is exactly the mistake the 07-13 burndown already made once: DELETING the showcase is the
 * wrong fix. The bug was never the data — it was that the data had no gate. The showcase belongs
 * HERE, in the one sanctioned demo dataset, where it can only ever reach demo mode.
 */
// Reachable Unsplash images so the approval previews render a real-looking visual (the
// preview modal renders content.image / content.thumbnail; without these the <img> is
// simply never emitted and the preview looks empty). Every deliverable type a "View a
// real example" link can point to (post / ad / video / GBP) is represented.
const IMG_SPIGOT = 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=600&h=600&fit=crop'
const IMG_BATH = 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600&h=600&fit=crop'
const IMG_EMERGENCY = 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&h=600&fit=crop'
const IMG_VIDEO = 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=800&h=450&fit=crop'

const pendingApprovals = [
  { id: 'dl-pa-1', agentId: 'social', agentType: 'social', type: 'post', deliverableType: 'facebook-post',
    title: 'Spring plumbing maintenance tips', status: 'pending', sourceAgent: 'social', createdAt: ts(0),
    content: { text: 'Spring is here! 🌱 Time to check your outdoor spigots for winter damage before the first big watering day…', hashtags: '#PlumberBoise #SpringMaintenance #TreasureValley', platforms: ['Facebook', 'Instagram'], scheduledDate: 'Tomorrow', image: IMG_SPIGOT } },
  { id: 'dl-pa-2', agentId: 'social', agentType: 'social', type: 'post', deliverableType: 'instagram-post',
    title: 'Before/after: bathroom remodel in Eagle', status: 'pending', sourceAgent: 'social', createdAt: ts(0),
    content: { text: 'Another beautiful bathroom remodel completed in Eagle. Swipe to see the before.', platforms: ['Instagram'], scheduledDate: 'In 2 days', image: IMG_BATH } },
  { id: 'dl-pa-3', agentId: 'nurture', agentType: 'nurture', type: 'nurture', deliverableType: 'sms',
    title: 'Win-back — 11 months since last service', status: 'pending', sourceAgent: 'nurture', createdAt: ts(1),
    content: { text: 'Hi Tom — it has been about a year since we serviced your water heater. Want me to get you on the schedule for a check-up?', platforms: ['SMS'], scheduledDate: 'Tomorrow' } },
  { id: 'dl-pa-4', agentId: 'google', agentType: 'google', type: 'post', deliverableType: 'gbp-post',
    title: 'Emergency plumbing — we answer at 2am', status: 'pending', sourceAgent: 'google', createdAt: ts(1),
    content: { text: 'Burst pipe at midnight? We answer. 24/7 emergency plumbing across the Treasure Valley.', platforms: ['Google Business Profile'], scheduledDate: 'In 3 days', image: IMG_EMERGENCY } },
  { id: 'dl-pa-5', agentId: 'ads', agentType: 'ads', type: 'ad', deliverableType: 'facebook-ad',
    title: 'Frozen pipes — 24/7 emergency ad', status: 'pending', sourceAgent: 'ads', createdAt: ts(1),
    content: { text: "Frozen pipes? Don't wait until it's too late. Treasure Valley Plumbing offers 24/7 emergency service across Boise, Meridian, and Eagle.", headline: '24/7 Emergency Plumbing', description: 'Fast, reliable emergency service across the Treasure Valley.', cta: 'Call Now', platforms: ['Facebook', 'Instagram'], scheduledDate: 'On approval', image: IMG_SPIGOT } },
  { id: 'dl-pa-6', agentId: 'video', agentType: 'video', type: 'video', deliverableType: 'youtube-short',
    title: 'How to Fix a Running Toilet in 5 Minutes — DIY', status: 'pending', sourceAgent: 'video', createdAt: ts(2),
    content: { videoTitle: 'How to Fix a Running Toilet in 5 Minutes — DIY', text: 'A quick DIY guide your customers will love — builds trust and drives calls.', videoDuration: '4:32', videoTags: ['PlumbingTips', 'DIY', 'TreasureValley'], platforms: ['YouTube'], scheduledDate: 'In 2 days', thumbnail: IMG_VIDEO, image: IMG_VIDEO,
      videoComments: [
        { at: '0:12', note: 'Add our logo intro here' },
        { at: '1:30', note: 'Mention the free inspection offer' },
        { at: '3:05', note: 'End with the phone number on screen' },
      ] } },
] as unknown as ClientData['pendingApprovals']

const approvalLogs = [
  { id: 'dl-ap-1', agentType: 'social', deliverableType: 'facebook-post', action: 'approved', channel: 'sms-link', timestamp: ts(1), contentBefore: null, contentAfter: null, editNotes: null, approvedBy: 'Mike Thompson', deliverableRef: `clients/${DEMO_CLIENT.id}/pendingApprovals/dl-da-1` },
  { id: 'dl-ap-2', agentType: 'google', deliverableType: 'review-response', action: 'auto-approved', channel: 'autopilot', timestamp: ts(2), contentBefore: null, contentAfter: null, editNotes: null, approvedBy: 'Mike Thompson', deliverableRef: `clients/${DEMO_CLIENT.id}/pendingApprovals/dl-da-2` },
  { id: 'dl-ap-3', agentType: 'video', deliverableType: 'youtube-video', action: 'edited-and-approved', channel: 'control-center', timestamp: ts(4), contentBefore: null, contentAfter: null, editNotes: 'Shortened the intro.', approvedBy: 'Mike Thompson', deliverableRef: `clients/${DEMO_CLIENT.id}/pendingApprovals/dl-da-3` },
] as unknown as ClientData['approvalLogs']

// The decided approval docs those log rows point at — so the showcase demonstrates the
// approved-vs-PUBLISHED distinction (2026-10-02): one published, one approved and waiting
// for its date, one with no publish record (shown neutrally as plain "Approved").
const decidedApprovals = [
  { id: 'dl-da-1', agentId: 'social', type: 'post', title: 'Winter pipe protection tips', status: 'approved',
    decidedAt: ts(1), createdAt: ts(2), publishStatus: 'published', publishedAt: ts(1) },
  { id: 'dl-da-2', agentId: 'google', type: 'review-response', title: 'Reply to a 5-star review', status: 'approved',
    decidedAt: ts(2), createdAt: ts(3), publishStatus: 'pending', scheduledFor: ts(-2) },
  { id: 'dl-da-3', agentId: 'video', type: 'video', title: '60-second drain clearing hack', status: 'approved',
    decidedAt: ts(4), createdAt: ts(5) },
] as unknown as ClientData['decidedApprovals']

// ⚠️ LINE ITEMS USE THE REAL SHAPE. They carried `{ description, amount }`, which
// is not a field on `InvoicingDocLineItem` (description · qty · unitPrice ·
// lineTotal, in CENTS) — the `as unknown as` cast below let it through. Anything
// reading a line item in demo mode therefore saw undefined where production has a
// number, so a feature could look broken here and work live, or the reverse. Found
// while testing document Duplicate, 2026-09-20.
// ⚠️ THIS FIXTURE WAS CAST, NOT CHECKED — `as unknown as` hid four real defects
// at once (found 2026-09-20 while testing document Duplicate):
//   · `lineItems` used `{ description, amount }`; the real shape is
//     description · qty · unitPrice · lineTotal, in CENTS. Anything reading a line
//     item saw `undefined` where production has a number.
//   · `totals` carried only `total`, so subtotal/tax/discount were undefined.
//   · `status: 'overdue'` is not an InvoicingDocStatus at all. An overdue invoice
//     is `sent` with a due date in the past — lateness is DERIVED, not stored.
//   · `publicToken` was absent, so the detail panel's preview iframe pointed at
//     `/doc/invoice/undefined`.
// It is `satisfies` now, so the compiler checks it. Demo mode is what prospects
// are shown on a call; a fixture that disagrees with production makes a working
// feature look broken there, or the reverse.
const invoicingDocs = [
  { id: 'dl-inv-1', type: 'invoice', number: 'INV-1042', status: 'paid',
    customerId: 'demo-cust-1', customerName: 'Sarah Martinez', publicToken: 'demo-token-1',
    totals: { subtotal: 45000, discountTotal: 0, taxRate: 0.06, taxAmount: 2700, total: 47700, amountPaid: 47700, amountDue: 0 },
    lineItems: [{ id: 'li-1', description: 'Water heater replacement', qty: 1, unitPrice: 45000, lineTotal: 45000 }],
    createdAt: ts(6), updatedAt: ts(6) },
  { id: 'dl-inv-2', type: 'quote', number: 'Q-0188', status: 'viewed',
    customerId: 'demo-cust-2', customerName: 'Tom Henderson', publicToken: 'demo-token-2',
    totals: { subtotal: 320000, discountTotal: 0, taxRate: 0.06, taxAmount: 19200, total: 339200, amountPaid: 0, amountDue: 339200 },
    lineItems: [{ id: 'li-1', description: 'Repipe — whole house', qty: 1, unitPrice: 320000, lineTotal: 320000 }],
    createdAt: ts(2), updatedAt: ts(2) },
  { id: 'dl-inv-3', type: 'invoice', number: 'INV-1041', status: 'sent',
    customerId: 'demo-cust-3', customerName: 'Linda Chen', publicToken: 'demo-token-3',
    totals: { subtotal: 78000, discountTotal: 0, taxRate: 0.06, taxAmount: 4680, total: 82680, amountPaid: 0, amountDue: 82680 },
    lineItems: [{ id: 'li-1', description: 'Drain cleaning + camera inspection', qty: 1, unitPrice: 78000, lineTotal: 78000 }],
    createdAt: ts(1), updatedAt: ts(1) },
  { id: 'dl-inv-4', type: 'invoice', number: 'INV-1040', status: 'paid',
    customerId: 'demo-cust-4', customerName: 'David Okafor', publicToken: 'demo-token-4',
    totals: { subtotal: 26500, discountTotal: 0, taxRate: 0.06, taxAmount: 1590, total: 28090, amountPaid: 28090, amountDue: 0 },
    lineItems: [{ id: 'li-1', description: 'Faucet install', qty: 1, unitPrice: 26500, lineTotal: 26500 }],
    createdAt: ts(9), updatedAt: ts(9) },
  { id: 'dl-inv-5', type: 'proposal', number: 'P-0042', status: 'signed',
    customerId: 'demo-cust-5', customerName: 'Emily Rossi', publicToken: 'demo-token-5',
    totals: { subtotal: 540000, discountTotal: 0, taxRate: 0.06, taxAmount: 32400, total: 572400, amountPaid: 0, amountDue: 572400 },
    lineItems: [{ id: 'li-1', description: 'Bathroom remodel — plumbing scope', qty: 1, unitPrice: 540000, lineTotal: 540000 }],
    createdAt: ts(4), updatedAt: ts(4) },
  { id: 'dl-inv-6', type: 'invoice', number: 'INV-1039', status: 'sent',
    customerId: 'demo-cust-6', customerName: 'James Park', publicToken: 'demo-token-6',
    totals: { subtotal: 51000, discountTotal: 0, taxRate: 0.06, taxAmount: 3060, total: 54060, amountPaid: 0, amountDue: 54060 },
    lineItems: [{ id: 'li-1', description: 'Sump pump replacement', qty: 1, unitPrice: 51000, lineTotal: 51000 }],
    createdAt: ts(14), updatedAt: ts(14) },
] satisfies ClientData['invoicingDocs']

const recurringContracts = [
  { id: 'dl-rc-1', customerName: 'Boise Ridge HOA', description: 'Quarterly backflow testing — 6 buildings', amount: 90000, frequency: 'quarterly', status: 'active', chargeCount: 3, nextCharge: ts(-35), createdAt: ts(120) },
  { id: 'dl-rc-2', customerName: 'Mercado Properties', description: 'Monthly drain maintenance', amount: 18000, frequency: 'monthly', status: 'active', chargeCount: 7, nextCharge: ts(-12), createdAt: ts(210) },
  { id: 'dl-rc-3', customerName: 'Riverside Dental', description: 'Semiannual plumbing inspection', amount: 35000, frequency: 'semiannual', status: 'paused', chargeCount: 1, nextCharge: null, createdAt: ts(90) },
] as unknown as ClientData['recurringContracts']

const customers = LEAD_NAMES.slice(0, 8).map((name, i) => ({
  id: `dl-cust-${i + 1}`, name, phone: `+1208555${String(2000 + i).padStart(4, '0')}`,
  email: `${name.split(' ')[0].toLowerCase()}@example.com`, createdAt: ts(i + 5),
})) as unknown as ClientData['customers']

const documentTemplates = [
  { id: 'dl-tpl-1', name: 'Standard Plumbing Quote', type: 'quote', version: 3, source: 'custom', publicPreview: { enabled: false } },
  { id: 'dl-tpl-2', name: 'Bathroom Remodel Proposal', type: 'proposal', version: 2, source: 'industry', publicPreview: { enabled: true } },
  { id: 'dl-tpl-3', name: 'Service Invoice', type: 'invoice', version: 5, source: 'custom', publicPreview: { enabled: false } },
] as unknown as ClientData['documentTemplates']

/**
 * Referrals for the Zoom showcase. Empty since this fixture was created
 * (2026-06-13), so Refer & Earn — one of the strongest pages to demo — showed an
 * empty state to every prospect.
 *
 * The numbers are chosen to tell ONE coherent story rather than to look big.
 *
 * ⚠️ REWRITTEN 2026-09-13. This said "7 active x $75 = $525 earned against this
 * fixture's $450 monthlyBill, so $450 lands on the bill and exactly $75 carries"
 * — arithmetic at a rate retired on 2026-08-09, when the credit became $37.50.
 * At the real rate 7 active earn $262.50 against a $450 bill, so all of it
 * applies and nothing carries. The carried-forward line showing nothing IS the
 * honest demo: it only triggers once earnings exceed the bill.
 *
 * One pending referral keeps the page honest — a list where every referral
 * succeeded is the fabricated-data bug wearing a friendlier hat.
 */

const demoReferrals = [
  ...REFERRAL_BUSINESSES.map((name, i) => ({
    id: `demo-referral-${i + 1}`,
    referrerClientId: 'demo-treasure-valley-plumbing',
    referredClientId: `demo-referred-${i + 1}`,
    referredBusinessName: name,
    referralCode: 'MIKE-TVP',
    status: 'active' as const,
    // ⚠️ DERIVED, NOT TYPED (2026-09-13). This was the literal `7500` with a
    // comment claiming it "matches the pricing SOT referral credit" — and it had
    // not matched since 2026-08-09, when the rate changed from $75 to $37.50.
    // The comment was the problem: it vouched for a number nobody re-checked.
    creditAmount: Math.round(REFERRAL_CREDIT * 100),
    creditActive: true,
    createdAt: null,
  })),
  {
    id: 'demo-referral-8',
    referrerClientId: 'demo-treasure-valley-plumbing',
    referredClientId: 'demo-referred-8',
    referredBusinessName: 'Star Valley Fencing',
    referralCode: 'MIKE-TVP',
    status: 'pending' as const,
    creditAmount: 0,
    creditActive: false,
    createdAt: null,
  },
];

const metrics = {
  leadsThisMonth: leads.length,
  // The demo fixture is deliberately CLEAN — every demo lead is a real arrived
  // lead from a real source, so the attributed list is the whole list. (This is
  // the fixture the canary runs against; see the note on demo-vs-real data in
  // [[feedback_an_alert_that_cries_wolf_is_pre_muted]].)
  attributedLeads: leads,
  testContactsExcluded: 0,
  appointmentsBooked: 5,
  callsAnswered: 14,
  postsPublished: 9,
  reviewsGenerated: 6,
  agentTasks: 29,
  estimatedRevenue: 5950,
  avgJobValue: 1190, // 5 booked x 1190 = 5950 estimatedRevenue (demo client has set their own figure)
  avgJobValueIsDefault: false,
  leadsBySource: { google: 3, phone: 2, facebook: 2, website: 3, referral: 2 },
  leadsByStatus: { new: 2, contacted: 2, booked: 4, won: 2, nurture: 2 },
} as ClientData['metrics']

/** The full ClientData fixture useClientData() returns in demo mode. */
export const DEMO_CLIENT_DATA: ClientData = {
  clientId: DEMO_CLIENT.id,
  client: DEMO_CLIENT,
  leads,
  actionItems,
  healthChecks: [],
  weeklyReports,
  agentLogs,
  businessProfileVars: [],
  businessProfileMeta: null,
  approvalLogs,
  pendingApprovals,
  decidedApprovals,
  invoicingDocs,
  invoicingConfig: null,
  invoicingPromoCodes: [],
  recurringContracts,
  customers,
  documentTemplates,
  sections: [],
  connectedAccounts: [],
  postOnboardingState: null,
  referrals: demoReferrals,
  metrics,
  loading: false,
  error: null,
}
